import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";

const SECTIONS = [
    {
        h: "1. Who we are",
        p: [
            "BuildEase is a South African residential construction intelligence platform operated by the BuildEase team. This policy explains how personal information is handled when you use the BuildEase website and platform, in line with the Protection of Personal Information Act (POPIA).",
            "Questions or requests can be sent to support@buildease.co.za at any time.",
        ],
    },
    {
        h: "2. Information we collect",
        p: [
            "Contact and enquiry information you provide — such as your name, email address, phone number and the content of your message.",
            "Project information you provide when creating a project or joining the pilot — such as the project location, the size and type of home you are planning, and whether you have architectural plans.",
            "Architectural documents you upload to your project (plans, drawings and schedules) — these belong to your project and are used to organise and interpret your project's information.",
            "Basic technical and usage information — such as pages visited and interactions with key features — collected without cookies or cross-site tracking.",
        ],
    },
    {
        h: "3. How your information is used",
        p: [
            "To respond to enquiries and provide support.",
            "To set up and operate your projects, interpret available documents and generate the insights you request.",
            "To improve the platform — understanding which capabilities are used helps guide development.",
            "To communicate platform updates that are relevant to your project or pilot participation.",
        ],
    },
    {
        h: "4. AI processing and its limitations",
        p: [
            "BuildEase uses AI to support construction insights. AI-generated information may contain errors and should be reviewed where appropriate.",
            "Outputs depend on the quality and completeness of the information supplied. Information is labelled by its state — Verified, Estimated or Assumed — so you can see what rests on what.",
        ],
    },
    {
        h: "5. Third-party service providers",
        p: [
            "BuildEase uses a small number of service providers to operate the platform — including a managed hosting and database provider, a payment processing provider (PayFast is the intended payment provider at launch; card details are processed by the payment provider and are never stored by BuildEase), and a managed email delivery provider for support communications.",
            "Service providers process information only as needed to operate the platform.",
        ],
    },
    {
        h: "6. Storage, security and retention",
        p: [
            "Project and enquiry information is stored on secure infrastructure with access controls. Data is retained while your account and projects are active, and for a reasonable period thereafter to meet legal obligations or resolve disputes.",
            "You can request deletion of your personal information and project data at any time, subject to legal retention requirements.",
        ],
    },
    {
        h: "7. Your rights (POPIA)",
        p: [
            "You have the right to access the personal information we hold about you, to request correction or deletion, to object to certain processing, and to lodge a complaint with the Information Regulator of South Africa.",
            "Requests can be sent to support@buildease.co.za.",
        ],
    },
    {
        h: "8. Security responsibilities we do not claim",
        p: [
            "BuildEase does not claim security certifications or regulatory approvals that have not been verified. No system can be guaranteed completely secure; we apply appropriate safeguards and treat reported issues seriously.",
        ],
    },
    {
        h: "9. Changes to this policy",
        p: [
            "This policy may be updated as the platform develops. The latest version is always available on this page.",
        ],
    },
];

const PrivacyPage = () => (
    <div className="min-h-screen bg-ink text-white">
        <Seo
            title="Privacy Policy | BuildEase"
            description="How BuildEase collects, uses and protects personal information, architectural documents and project information — POPIA-conscious by design."
        />
        <Navbar />
        <main>
            <section className="relative overflow-hidden bg-ink pt-[128px] lg:pt-[168px]">
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                    <motion.div variants={fadeUp} initial="hidden" animate="show">
                        <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                            <span className="h-1.5 w-1.5 bg-accent" />
                            PRIVACY POLICY
                        </p>
                        <h1
                            data-testid="privacy-headline"
                            className="mt-8 font-display text-4xl font-semibold leading-[1.08] text-white md:text-5xl"
                        >
                            Your information, handled with care
                            <span className="text-accent">.</span>
                        </h1>
                        <p className="mt-4 font-mono text-[13px] tracking-[0.22em] text-cad">
                            LAST UPDATED — NOVEMBER 2026
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="relative py-14 lg:py-20">
                <div className="mx-auto max-w-[900px] px-6 md:px-10 lg:px-16">
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                    >
                        {SECTIONS.map((s) => (
                            <motion.div key={s.h} variants={fadeUp} className="mt-10 first:mt-0">
                                <h2 className="font-display text-2xl font-semibold text-white">
                                    {s.h}
                                </h2>
                                {s.p.map((para, i) => (
                                    <p key={i} className="mt-4 text-[16px] leading-relaxed text-mist">
                                        {para}
                                    </p>
                                ))}
                            </motion.div>
                        ))}
                        <motion.p variants={fadeUp} className="mt-14 border-t border-white/10 pt-8 text-[15px] leading-relaxed text-mist">
                            Related:{" "}
                            <Link to="/terms" data-testid="privacy-terms-link" className="text-accent hover:text-[#ff7026]">
                                Terms &amp; Conditions
                            </Link>{" "}
                            ·{" "}
                            <Link to="/trust" data-testid="privacy-trust-link" className="text-accent hover:text-[#ff7026]">
                                Trust &amp; Transparency
                            </Link>
                        </motion.p>
                    </motion.div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
);

export default PrivacyPage;
